import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { UserModel, UserProfileModel } from 'src/app/models/profile.model';
import { ProfileService } from 'src/app/pages/profile/profile.service';
import { environment } from 'src/environments/environment';

import { SafeLockService } from './safelock.service';

@Injectable()
export class AuthStore {
  private userSubject = new BehaviorSubject<UserModel>({} as UserModel);
  user$: Observable<UserModel> = this.userSubject.asObservable();

  private userProfileSubject = new BehaviorSubject<UserProfileModel>({} as UserProfileModel);
  userProfile$: Observable<UserProfileModel> = this.userProfileSubject.asObservable();

  private userProfileLoaded = new BehaviorSubject<boolean>(false);
  userProfileLoaded$ = this.userProfileLoaded.asObservable();

  private isSafeLockActivated = new BehaviorSubject<boolean | undefined>(undefined);
  isSafeLockActivated$ = this.isSafeLockActivated.asObservable();

  private isSafeLockEnabled = new BehaviorSubject<boolean | undefined>(undefined);
  isSafeLockEnabled$ = this.isSafeLockEnabled.asObservable();

  private tokenSubject = new BehaviorSubject<string>('');
  token$: Observable<string> = this.tokenSubject.asObservable();

  isLoggedIn$!: Observable<boolean>;

  token!: string;
  isFirstLogin!: boolean;

  constructor(
    private keycloakService: KeycloakService,
    private translateService: TranslateService,
    private profileService: ProfileService,
    private safeLockService: SafeLockService,
  ) {
    this.getUserInfo();
    this.getToken();
    // const storedFirstLoginDate = localStorage.getItem('firstLoginDate');

    // if (storedFirstLoginDate !== null) {
    //   this.isFirstLogin = true;
    // } else {
    //   this.isFirstLogin = false;
    // }
    this.getUserProfile();
  }

  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('token', token);

    if (this.isFirstLogin) {
      this.isFirstLogin = false;
      const currentDate = new Date();
      localStorage.setItem('firstLoginDate', currentDate.toISOString());
    }
  }

  checkFirstLogin(): boolean {
    return this.isFirstLogin;
  }

  getToken(): any {
    // const { token } = this.keycloakService.getKeycloakInstance();

    // if (token) this.setToken(token);

    return this.keycloakService.getKeycloakInstance().token;
  }

  checkTokenExpiration(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if (this.checkFirstLogin() && this.is24HoursPassed()) {
        observer.next(false);
        observer.complete();
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }

  private is24HoursPassed(): boolean {
    const storedDate = localStorage.getItem('firstLoginDate');
    const firstLoginDate = storedDate ? new Date(storedDate) : new Date();

    const currentDate = new Date();
    const timeDifference = currentDate.getTime() - firstLoginDate.getTime();
    const hoursDifference = timeDifference / (1000 * 3600);

    return hoursDifference >= 24;
  }

  getUserInfo(): void {
    this.keycloakService.loadUserProfile().then((user) => {
      const userInfo = {} as UserModel;
      userInfo.email = user.email as string;
      userInfo.firstName = user.firstName as string;
      userInfo.lastName = user.lastName as string;
      userInfo.wholeName = userInfo.firstName + ' ' + userInfo.lastName;
      userInfo.isActivated = user.emailVerified as boolean;
      const { locale } = this.keycloakService.getKeycloakInstance().tokenParsed ?? {};
      if (locale) {
        userInfo.locale = locale.toUpperCase();
      }
      this.userSubject.next(userInfo);
      this.isLoggedIn$ = this.user$.pipe(map((user) => Object.keys(user).length > 0));
      this.safeLockService
        .isSafelockActivated(userInfo.email)
        .then((isSafeLockActivated) => {
          this.isSafeLockActivated.next(isSafeLockActivated);
        })
        .catch(() => {
          this.isSafeLockActivated.next(false);
        });
    });
  }

  getUserProfile(): void {
    this.profileService.getUserInformation().subscribe({
      next: (res: UserProfileModel) => {
        this.userProfileLoaded.next(true);
        this.userProfileSubject.next(res);
        const language = res.locale && res.locale === 'ENG' ? 'en' : 'tr';
        this.translateService.setDefaultLang(language);
      },
    });
  }

  setSafeLockActivated(isSafeLockActivated: boolean): void {
    this.isSafeLockActivated.next(isSafeLockActivated);
  }

  setSafeLockEnabled(isSafeLockEnabled: boolean): void {
    this.isSafeLockEnabled.next(isSafeLockEnabled);
  }

  logout(): void {
    if (window.location.origin !== 'http://localhost:4500') {
      this.keycloakService.logout(environment.apiUrl + '/genel-bakis');
    } else {
      this.keycloakService.logout(window.location.origin + '/genel-bakis');
    }
    this.userSubject.next({} as UserModel);
  }
}
